import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { EnhancedButtonStatus } from '@components/_old/EnhancedButton';
import { useMutation } from '@apollo/client';
import {
  createAmendmentMutation,
  createCancellationAmendmentMutation,
} from './queries';
import { useNavigate, useParams } from 'react-router-dom';
import { getError } from '@utils/graph-utils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import DateService from '@services/DateService';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { FormFieldType } from '@components/dynamic-form/types/inputTypes';

interface IAddAmendmentDrawerProps {
  onClose: () => void;
  open: boolean;
  data: Record<string, Record<string, string>>;
  policyDetails: Record<string, string>;
}

const AddAmendmentDrawer: React.FC<IAddAmendmentDrawerProps> = ({
  open,
  onClose,
  data,
  policyDetails,
}) => {
  const { id: currentPolicyId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [createAmendment] = useMutation(createAmendmentMutation);
  const [createCancellationAmendment] = useMutation(
    createCancellationAmendmentMutation
  );

  const inputsForm = useMemo((): IDynamicFormWidgetSection[] => {
    return [
      {
        inputs: {
          type: {
            name: 'type',
            title: 'Type',
            placeholder: 'Amendment Type',
            type: FormFieldType.select,
            value: '',
            required: true,
            selectOptions: data.types,
          },
          subtype: {
            name: 'subtype',
            title: 'Sub Type',
            placeholder: 'Amendment Sub Type',
            type: FormFieldType.select,
            value: '',
            selectOptions: data.subTypes,
            conditionalRequired: (values) => {
              return values.type !== 'POLICY_CANCELLATION';
            },
            conditionalHidden: (values) => {
              return values.type === 'POLICY_CANCELLATION';
            },
          },
          policyNumber: {
            name: 'policyNumber',
            title: 'Policy Number',
            type: FormFieldType.text,
            value: policyDetails.policyNumber,
            conditionalRequired: (values) => {
              return values.type === 'POLICY_CANCELLATION';
            },
            conditionalHidden: (values) => {
              return values.type !== 'POLICY_CANCELLATION';
            },
            disabled: true,
          },
          policyInceptionDate: {
            name: 'policyInceptionDate',
            title: 'Policy Inception Date',
            type: FormFieldType.date,
            value: policyDetails.policyEffectiveDate,
            conditionalRequired: (values) => {
              return values.type === 'POLICY_CANCELLATION';
            },
            conditionalHidden: (values) => {
              return values.type !== 'POLICY_CANCELLATION';
            },
            disabled: true,
          },
          cancellationEffectiveDate: {
            name: 'cancellationEffectiveDate',
            title: 'Cancellation Effective Date',
            placeholder: 'Cancellation Effective Date',
            type: FormFieldType.date,
            value: '',
            conditionalRequired: (values) => {
              return values.type === 'POLICY_CANCELLATION';
            },
            conditionalHidden: (values) => {
              return values.type !== 'POLICY_CANCELLATION';
            },
            minDate:
              policyDetails.access === 'POS'
                ? new Date()
                : new Date(policyDetails.policyEffectiveDate),
            maxDate: new Date(policyDetails.policyExpiryDate),
          },
          cancellationReason: {
            name: 'cancellationReason',
            title: 'Cancellation Reason',
            placeholder: 'Reason',
            type: FormFieldType.select,
            value: '',
            selectOptions: data.cancellationReasons,
            conditionalRequired: (values) => {
              return values.type === 'POLICY_CANCELLATION';
            },
            conditionalHidden: (values) => {
              return values.type !== 'POLICY_CANCELLATION';
            },
          },
          cancellationIssuanceDate: {
            name: 'cancellationIssuanceDate',
            title: 'Cancellation Issuance Date',
            type: FormFieldType.date,
            value: new Date(),
            conditionalRequired: (values) => {
              return values.type === 'POLICY_CANCELLATION';
            },
            conditionalHidden: (values) => {
              return values.type !== 'POLICY_CANCELLATION';
            },
            disabled: true,
          },
        },
      },
    ];
  }, [data]);

  const submitForm = async (values: Record<string, any>) => {
    setFormDisabled(true);
    setSubmitButtonState('loading');

    let data, errors;

    try {
      const variables: Record<string, string> = {
        policyID: currentPolicyId,
        amendmentType: values.type,
      };

      if (values.type !== 'POLICY_CANCELLATION') {
        variables.amendmentSubType = values.subtype;
      }

      const cancellationVariables: Record<string, string> = {
        policyID: currentPolicyId,
        policyEffectiveDate: DateService.formatDateBackend(
          values.policyInceptionDate
        ),
        cancellationEffectiveDate: DateService.formatDateBackend(
          values.cancellationEffectiveDate
        ),
        cancellationIssuanceDate: DateService.formatDateBackend(
          values.cancellationIssuanceDate
        ),
        cancellationReason: values.cancellationReason,
      };

      if (values.type === 'POLICY_CANCELLATION') {
        ({ data, errors } = await createCancellationAmendment({
          variables: cancellationVariables,
          errorPolicy: 'all',
        }));
      } else {
        ({ data, errors } = await createAmendment({
          variables,
          errorPolicy: 'all',
        }));
      }

      const createdAmendmentId =
        data?.production?.actions?.createAmendment?.id ??
        data?.production?.actions?.createCancellationAmendment?.id;

      if (createdAmendmentId) {
        toast.success(
          <ToastSuccessMessage>
            Amendment successfully created
          </ToastSuccessMessage>
        );
        setTimeout(() => {
          navigate(`/production/amendment/${createdAmendmentId}`);
        }, 750);
      } else {
        setSubmitButtonState(undefined);
        const errorMessage = getError({ errors });
        toast.error(<ToastErrorMessage>{errorMessage}</ToastErrorMessage>);
      }
    } catch (error) {
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{getError(error)}</ToastErrorMessage>);
    } finally {
      setFormDisabled(false);
    }
  };

  return (
    <DynamicFormDrawer
      title={'Add Amendment'}
      onClose={() => onClose()}
      isOpen={open}
      loading={!open}
      sections={inputsForm}
      onSubmit={(v) => submitForm(v)}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
    />
  );
};

export default AddAmendmentDrawer;
